import { useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { AccountingBasis } from '@modules-deprecated/app/organization/types/accountingBasis'

import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { InvoiceStatus } from '../../enums/invoiceStatus'
import { fetchInvoice } from '../../query-api'
import type { RelatedInvoice } from '../../types/relatedInvoice'
import { getInvoiceQueryKey } from '../../utils/getInvoiceQueryKey'

interface UseCanCreateFinalInvoiceOptions {
  depositInvoiceId?: number
  existingFinalInvoiceDraft?: RelatedInvoice
  modalId?: string
}

export const useCanCreateFinalInvoice = ({
  depositInvoiceId,
  existingFinalInvoiceDraft,
  modalId,
}: UseCanCreateFinalInvoiceOptions) => {
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()
  const { open: openBlockModal } = useModal(modalId ?? '')

  const canCreateFinalInvoice = useCallback(async () => {
    if (existingFinalInvoiceDraft) {
      openBlockModal()
      return false
    }

    if (organization?.accountingBasis !== AccountingBasis.Cash) {
      return true
    }

    if (!depositInvoiceId) {
      return false
    }

    const depositInvoiceIdString = depositInvoiceId.toString()

    const { data: depositInvoice } = await queryClient.fetchQuery(
      getInvoiceQueryKey(depositInvoiceIdString, InvoiceDocumentType.Invoice),
      () => fetchInvoice(depositInvoiceIdString),
    )

    if (depositInvoice?.status?.status === InvoiceStatus.Paid) {
      return true
    }

    openBlockModal()
    return false
  }, [depositInvoiceId, existingFinalInvoiceDraft, openBlockModal, organization?.accountingBasis, queryClient])

  return { canCreateFinalInvoice }
}
